import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Image,
  Input,
  Select,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import {
  REQUEST_CREATE_MASTER_GIFTS,
  REQUEST_MASTER_GIFTS,
  REQUEST_UPDATE_MASTER_GIFTS,
} from "../../store/giftcards/giftcardsActionTypes";

interface GiftsDrawerProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
}

interface ImageState {
  file: File | null;
  preview: string | null;
}

const validationSchema = Yup.object().shape({
  bannerImage: Yup.mixed().required("Banner image is required"),
  //   .test(
  //     "fileFormat",
  //     "Unsupported file type. Supported types: .png, .jpg, .jpeg, .pdf, .doc, .docx, .xls, .xlsx",
  //     (value) =>
  //       !value ||
  //       ["image/png", "image/jpeg", "image/jpg", "application/pdf"].includes(
  //         value.type
  //       )
  //   ),
  themeName: Yup.string().required("theme is required"),
});

const GiftsMasterEditDrawer: React.FC<GiftsDrawerProps> = ({
  isOpen,
  onClose,
  event,
}) => {
  const toast = useToast();

  const [bannerImage, setBannerImage] = useState<ImageState>(() => {
    if (event?.themeImg) {
      return {
        file: null,
        preview: event.themeImg, // Use the existing image URL as preview
      };
    }
    return {
      file: null,
      preview: "",
    };
  });

  const [isdisabled, setDisabled] = useState(false);

  const dispatch = useDispatch();

  // console.log("event........", event);

  let initialValues = {
    bannerImage: event?.themeImg || null,
    themeName: event?.theme || "",
    bannerImageName: event?.themeImgId || "",
  };

  // const handleImageUpload = async (
  //   event: React.ChangeEvent<HTMLInputElement>,
  //   setFieldValue: (field: string, value: any) => void
  // ) => {
  //   setDisabled(true);
  //   const file = event.target.files?.[0] || null;
  //   if (file) {
  //     setBannerImage({
  //       file,
  //       preview: URL.createObjectURL(file),
  //     });

  //     setFieldValue("bannerImage", file);

  //     try {
  //       const formData = new FormData();
  //       formData.append("file", file);

  //       const response = await axios.post(
  //         `${process.env.REACT_APP_BASE_URL}/upload/media`,
  //         formData,
  //         {
  //           headers: {
  //             "Content-Type": "multipart/form-data",
  //           },
  //         }
  //       );

  //       setFieldValue("bannerImageName", response.data.data._id || file.name);
  //       if (response) {
  //         setDisabled(false);
  //       }
  //     } catch (error) {
  //       setDisabled(false);
  //       console.error("Image upload failed:", error);
  //     }
  //   }
  // };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setDisabled(true);
    const file = event.target.files?.[0] || null;

    if (file) {
      const image = document.createElement("img");
      const objectUrl = URL.createObjectURL(file);

      try {
        // Wait for image to load and check dimensions
        await new Promise<void>((resolve, reject) => {
          image.onload = () => {
            console.log("Actual image dimensions:", {
              width: image.naturalWidth,
              height: image.naturalHeight,
            });

            // Check for required dimensions
            if (image.naturalWidth !== 320 || image.naturalHeight !== 150) {
              toast({
                title: "Invalid Image Dimensions",
                description: `Image dimensions are ${image.naturalWidth}px × ${image.naturalHeight}px. Required: 320px × 150px`,
                status: "error",
                isClosable: true,
                position: "top-right",
              });
              setDisabled(false);
              URL.revokeObjectURL(objectUrl);
              reject(new Error("Invalid dimensions"));
              return;
            }
            resolve();
          };
          image.onerror = () => reject(new Error("Failed to load image"));
          image.src = objectUrl;
        });

        // If dimensions are correct, proceed with upload
        setBannerImage({
          file,
          preview: objectUrl,
        });

        setFieldValue("bannerImage", file);

        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/upload/media`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        setFieldValue("bannerImageName", response.data.data._id || file.name);
        if (response) {
          setDisabled(false);
        }
      } catch (error) {
        console.error("Error in image upload:", error);
        if (error instanceof Error && error.message !== "Invalid dimensions") {
          // Only show error toast for non-dimension errors
          toast({
            title: "Error",
            description: "Failed to upload image",
            status: "error",
            isClosable: true,
            position: "top-right",
          });
        }
        setDisabled(false);
      } finally {
        // if (error instanceof Error && error.message !== "Invalid dimensions") {
        URL.revokeObjectURL(objectUrl);
        // }
      }
    }
  };

  const removeImage = (setFieldValue: (field: string, value: any) => void) => {
    setBannerImage({
      file: null,
      preview: "",
    });
    setFieldValue("bannerImage", null);
    setFieldValue("bannerImageName", "");
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    if (!values) {
      return;
    }

    try {
      const payload = {
        name: values.themeName,
        image: values.bannerImageName,
      };

      if (event) {
        dispatch({
          type: REQUEST_UPDATE_MASTER_GIFTS,
          data: { payload, _id: event.key },
          onSuccess: async (response: any) => {
            if (response.data) {
              dispatch({ type: REQUEST_MASTER_GIFTS });
              toast({
                title: "Success.",
                description: `${response.data.message}`,
                status: "success",
                isClosable: true,
                position: "top-right",
              });
            }
          },
        });
      } else {
        await dispatch({
          type: REQUEST_CREATE_MASTER_GIFTS,
          payload,
          onSuccess: async (response: any) => {
            if (response.data) {
              dispatch({ type: REQUEST_MASTER_GIFTS });
              toast({
                title: "Success.",
                description: `${response.data.message}`,
                status: "success",
                isClosable: true,
                position: "top-right",
              });
            }
          },
        });
      }

      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `${error.message || "An unexpected error occurred."}`,
        status: "error",
        isClosable: true,
        position: "top-right",
      });
      onClose();
    } finally {
      setSubmitting(false);
      onClose();
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottom="1px solid #E0E0E0" background="#e244411a">
          {!event ? "Add gift card template" : "Update gift card template"}
        </DrawerHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, errors, touched }) => (
            <Form
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <DrawerBody>
                <VStack spacing={4}>
                  {/* Banner Image Section */}

                  <FormControl
                    isInvalid={!!errors.bannerImage && !!touched?.bannerImage}
                  >
                    <FormLabel>Add theme image</FormLabel>
                    <Button
                      leftIcon={<FiUpload />}
                      onClick={() =>
                        document.getElementById("banner-upload")?.click()
                      }
                      variant="ghost"
                      color="#E24441"
                      border="1px dashed #E24441"
                      p={4}
                      borderRadius="md"
                    >
                      Upload Image
                    </Button>
                    <Input
                      id="banner-upload"
                      type="file"
                      accept="image/*"
                      display="none"
                      onChange={(e) => handleImageUpload(e, setFieldValue)}
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      You can add only 1 image. Supported file types are .png,
                      .jpg, and image dimensions must be exactly 320px × 150px
                    </Text>
                    {bannerImage.preview && (
                      <Grid templateColumns="repeat(1, 1fr)" gap={2} mt={2}>
                        <GridItem position="relative">
                          <Image
                            src={bannerImage.preview}
                            alt="Banner Preview"
                            boxSize="100px"
                            objectFit="contain"
                            borderRadius="6px"
                          />
                          <IconButton
                            aria-label="Remove image"
                            color="#E24441"
                            icon={<RiDeleteBin6Line />}
                            size="sm"
                            position="absolute"
                            top={2}
                            right={2}
                            onClick={() => removeImage(setFieldValue)}
                          />
                        </GridItem>
                      </Grid>
                    )}
                    <FormErrorMessage>
                      {errors.bannerImage
                        ? errors.bannerImage.toString()
                        : null}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.themeName && !!touched?.themeName}
                  >
                    <FormLabel>Enter theme</FormLabel>
                    <Field
                      as={Input}
                      name="themeName"
                      placeholder="Enter theme name"
                    />
                    <FormErrorMessage>
                      {errors.themeName ? errors.themeName.toString() : null}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>
              </DrawerBody>

              <DrawerFooter borderTopWidth="1px">
                <Button
                  variant="outline"
                  mr={3}
                  border="1px solid #EEE"
                  borderRadius="6px"
                  color="#424242"
                  fontSize="sm"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  background="#E24441"
                  fontSize="sm"
                  color="#fff"
                  type="submit"
                  _hover={{ background: "#E24441" }}
                  isLoading={isSubmitting}
                  disabled={isdisabled}
                  cursor={isdisabled ? "not-allowed" : ""}
                >
                  {!event ? "Add gift template" : "Update gift template"}
                </Button>
              </DrawerFooter>
            </Form>
          )}
        </Formik>
      </DrawerContent>
    </Drawer>
  );
};

export default GiftsMasterEditDrawer;
